/* Klaviyo owns review data and rendering. This module owns only the disclosure. */
(() => {
  'use strict';

  const selector = '[data-review-disclosure]';
  const nativeListSelector = '.kl_reviews__reviews_list';
  const nativeSummarySelector = '.kl_reviews__summary';
  const timeoutMs = 10000;

  function initialize(context = document) {
    const disclosures = [...context.querySelectorAll(selector)];
    if (context.matches?.(selector)) disclosures.unshift(context);
    disclosures.forEach((disclosure) => {
      if (disclosure.kayozReviewController) return;
      const toggle = disclosure.querySelector('[data-review-toggle]');
      const panel = disclosure.querySelector('[data-review-panel]');
      const status = disclosure.querySelector('[data-review-status]');
      const loading = disclosure.querySelector('[data-review-loading]');
      const failure = disclosure.querySelector('[data-review-error]');
      const retry = disclosure.querySelector('[data-review-reload]');
      if (!toggle || !panel) return;

      let timer = null;
      let frame = null;
      let destroyed = false;
      let state = '';
      let scrollWhenReady = false;
      let photoOpener = null;
      let photoWasOpen = false;
      const originalOpenLabel = disclosure.dataset.labelOpen || 'Read all verified reviews';

      const clearTimer = () => {
        window.clearTimeout(timer);
        timer = null;
      };
      const scrollToPanel = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          if (destroyed) return;
          const target = panel.querySelector(nativeSummarySelector) || panel;
          target.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
          frame = null;
        });
      };

      const setState = (next) => {
        if (state === next || destroyed) return;
        state = next;
        disclosure.dataset.reviewState = next;
        panel.setAttribute('aria-busy', String(next === 'loading'));
        if (status) status.hidden = next === 'ready' || next === 'empty';
        if (loading) loading.hidden = next !== 'loading';
        if (failure) failure.hidden = next !== 'error';
        toggle.hidden = next === 'empty';
        if (next !== 'loading') clearTimer();
        if (next === 'ready' && scrollWhenReady && disclosure.dataset.reviewsExpanded === 'true') {
          scrollWhenReady = false;
          scrollToPanel();
        }
      };

      const inspectNative = () => {
        if (destroyed) return;
        // Check the native render, never a timeout or a substitute review count.
        if (panel.querySelector(nativeListSelector) && panel.querySelector(nativeSummarySelector)) {
          setState('ready');
          const count = panel.querySelector('.kl_reviews__summary__stars__count')?.textContent.match(/[\d,]+/)?.[0];
          if (count && originalOpenLabel.includes('[count]')) {
            disclosure.dataset.labelOpen = originalOpenLabel.replaceAll('[count]', count);
            if (disclosure.dataset.reviewsExpanded !== 'true') toggle.textContent = disclosure.dataset.labelOpen;
          }
          return;
        }
        // Klaviyo renders one bare empty div after a successful zero-review
        // response when its configured empty state is hidden. An unmounted
        // host has no child element and must still time out as a load failure.
        const host = panel.querySelector('#fulfilled-reviews-all, #klaviyo-reviews-all');
        const emptyRender = host?.firstElementChild;
        if (host?.children.length === 1 && emptyRender.tagName === 'DIV'
          && emptyRender.attributes.length === 0 && emptyRender.childNodes.length === 0) {
          setState('empty');
          return;
        }
        if (!state) setState('loading');
      };

      const startTimer = () => {
        if (state !== 'loading' || timer !== null) return;
        timer = window.setTimeout(() => {
          timer = null;
          inspectNative();
          if (state === 'loading') setState('error');
        }, timeoutMs);
      };

      const update = (expanded) => {
        disclosure.dataset.reviewsExpanded = String(expanded);
        disclosure.dataset.reviewReady = 'true';
        toggle.setAttribute('aria-expanded', String(expanded));
        panel.hidden = false;
        panel.inert = !expanded;
        panel.setAttribute('aria-hidden', String(!expanded));
        toggle.textContent = expanded
          ? disclosure.dataset.labelClose || 'Show fewer reviews'
          : disclosure.dataset.labelOpen || originalOpenLabel;
        if (expanded) {
          inspectNative();
          startTimer();
          window.requestAnimationFrame(() => {
            if (!destroyed) window.dispatchEvent(new Event('resize'));
          });
        } else {
          scrollWhenReady = false;
          if (panel.contains(document.activeElement)) toggle.focus({ preventScroll: true });
        }
      };

      const isReviewHash = () => {
        let hash;
        try { hash = decodeURIComponent(window.location.hash.slice(1)); } catch { return false; }
        if (!hash) return false;
        return [disclosure.id, panel.id, 'fulfilled-reviews-all', 'klaviyo-reviews-all', 'kl_reviews_tabs__reviews_tab'].includes(hash)
          || panel.contains(document.getElementById(hash));
      };
      const openFromLink = () => {
        update(true);
        panel.focus({ preventScroll: true });
        scrollWhenReady = state === 'loading' || state === 'error';
        scrollToPanel();
      };
      const onToggle = () => update(disclosure.dataset.reviewsExpanded !== 'true');
      const onPhotoClick = (event) => {
        const button = event.target.closest?.('button, [role="button"]');
        if (!button?.querySelector('img') || !button.closest('#klaviyo-product-reviews-wrapper')) return;
        photoOpener = button;
        photoWasOpen = false;
      };
      const onPhotoKey = (event) => {
        if (event.key === 'Enter' || event.key === ' ') onPhotoClick(event);
      };
      // Klaviyo portals its photo viewer directly into body. Restore the
      // originating button when that native modal closes, without scrolling.
      const modalObserver = new MutationObserver(() => {
        if (!photoOpener || destroyed) return;
        const open = !!document.querySelector('.kl_reviews__lightbox_background');
        if (photoWasOpen && !open) {
          if (photoOpener.isConnected && disclosure.dataset.reviewsExpanded === 'true') {
            photoOpener.focus({ preventScroll: true });
          }
          photoOpener = null;
        }
        photoWasOpen = open;
      });
      const onTitleRating = (event) => {
        if (!event.target.closest?.('.kayoz-pdp__rating .klaviyo-star-rating-widget, .kayoz-pdp__rating .fulfilled-star-rating-widget')) return;
        event.preventDefault();
        event.stopPropagation();
        openFromLink();
      };
      const onHash = () => { if (isReviewHash()) openFromLink(); };
      const onReload = () => {
        const url = new URL(window.location.href);
        url.hash = panel.id;
        window.history.replaceState(window.history.state, '', url.href);
        window.location.reload();
      };

      const observer = new MutationObserver(inspectNative);
      observer.observe(panel, { childList: true, subtree: true, characterData: true });
      modalObserver.observe(document.body, { childList: true });
      toggle.addEventListener('click', onToggle);
      panel.addEventListener('click', onPhotoClick, true);
      panel.addEventListener('keydown', onPhotoKey, true);
      retry?.addEventListener('click', onReload);
      document.addEventListener('click', onTitleRating, true);
      window.addEventListener('hashchange', onHash);
      panel.tabIndex = -1;
      inspectNative();
      update(window.matchMedia('(min-width: 960px)').matches || isReviewHash());
      if (isReviewHash()) openFromLink();

      disclosure.kayozReviewController = {
        destroy() {
          destroyed = true;
          clearTimer();
          window.cancelAnimationFrame(frame);
          observer.disconnect();
          modalObserver.disconnect();
          toggle.removeEventListener('click', onToggle);
          panel.removeEventListener('click', onPhotoClick, true);
          panel.removeEventListener('keydown', onPhotoKey, true);
          retry?.removeEventListener('click', onReload);
          document.removeEventListener('click', onTitleRating, true);
          window.removeEventListener('hashchange', onHash);
          delete disclosure.kayozReviewController;
        },
      };
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(selector).forEach((disclosure) => disclosure.kayozReviewController?.destroy());
    if (event.target.matches?.(selector)) event.target.kayozReviewController?.destroy();
  });
})();
